import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:60401/api';

interface DataSource {
  id: number;
  name: string;
  source_key: string;
  api_url: string;
  auth_type: string;
  auth_key: string | null;
  is_active: boolean;
  last_fetched_at: string | null;
  created_at: string | null;
}

interface EditForm {
  name: string;
  source_key: string;
  api_url: string;
  auth_type: string;
  auth_key: string;
  auth_value: string;
  is_active: boolean;
}

const EMPTY_FORM: EditForm = {
  name: '', source_key: '', api_url: '',
  auth_type: 'none', auth_key: '', auth_value: '', is_active: true,
};

const COL: React.CSSProperties = { padding: '10px 12px', fontSize: '0.75rem', whiteSpace: 'nowrap', textAlign: 'left' };
const HDR: React.CSSProperties = { ...COL, color: '#4A5068', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #1E2330' };
const INPUT: React.CSSProperties = { width: '100%', padding: '8px 12px', backgroundColor: '#1E2330', border: '1px solid #2A3040', borderRadius: '6px', color: '#E8EAED', fontSize: '0.8rem', outline: 'none' };
const LABEL: React.CSSProperties = { display: 'block', fontSize: '0.65rem', fontWeight: 600, color: '#7B8399', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' };

export default function SourcesPage() {
  const [sources, setSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<EditForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => {
    fetch(`${API_BASE}/market/data-sources`)
      .then(res => res.json())
      .then(setSources)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const startEdit = (source: DataSource) => {
    setCreating(false);
    setEditId(source.id);
    setForm({
      name: source.name,
      source_key: source.source_key,
      api_url: source.api_url,
      auth_type: source.auth_type,
      auth_key: source.auth_key || '',
      auth_value: '',
      is_active: source.is_active,
    });
  };

  const startCreate = () => {
    setEditId(null);
    setCreating(true);
    setForm(EMPTY_FORM);
  };

  const cancel = () => {
    setEditId(null);
    setCreating(false);
  };

  const save = async () => {
    setSaving(true);
    const url = creating
      ? `${API_BASE}/market/data-sources`
      : `${API_BASE}/market/data-sources/${editId}`;
    const method = creating ? 'POST' : 'PUT';

    const body: any = { ...form };
    if (!body.auth_value) delete body.auth_value; // don't overwrite with empty

    await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    setSaving(false);
    setEditId(null);
    setCreating(false);
    load();
  };

  if (loading) {
    return <div style={{ color: '#4A5068', padding: '40px', textAlign: 'center' }}>Lade API Quellen...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '4px' }}>
            <i className="fa-solid fa-plug" style={{ marginRight: '10px', color: '#00D97E' }}></i>API Quellen
          </h1>
          <p style={{ color: '#7B8399', fontSize: '0.8rem' }}>{sources.length} Quellen konfiguriert</p>
        </div>
        <button
          onClick={startCreate}
          style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            backgroundColor: '#00D97E', color: '#0B0E14', fontSize: '0.8rem',
            fontWeight: 700, cursor: 'pointer',
          }}
        >
          + Neue Quelle
        </button>
      </div>

      <div style={{
        backgroundColor: '#151921', border: '1px solid #1E2330',
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={HDR}>Name</th>
              <th style={HDR}>Key</th>
              <th style={HDR}>API URL</th>
              <th style={HDR}>Auth</th>
              <th style={{ ...HDR, textAlign: 'center' }}>Status</th>
              <th style={HDR}>Letzter Abruf</th>
              <th style={HDR}>Erstellt</th>
              <th style={{ ...HDR, textAlign: 'center', width: '80px' }}></th>
            </tr>
          </thead>
          <tbody>
            {sources.map(source => (
              <React.Fragment key={source.id}>
                <tr
                  style={{ borderBottom: editId === source.id ? 'none' : '1px solid #1E2330' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1A1F2B'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td style={{ ...COL, fontWeight: 600 }}>{source.name}</td>
                  <td style={COL}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 700,
                      backgroundColor: '#8B5CF620', color: '#8B5CF6', fontFamily: 'monospace',
                    }}>
                      {source.source_key}
                    </span>
                  </td>
                  <td style={{ ...COL, color: '#7B8399', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {source.api_url}
                  </td>
                  <td style={COL}>
                    <span style={{
                      padding: '2px 6px', borderRadius: '4px', fontSize: '0.6rem', fontWeight: 600,
                      backgroundColor: source.auth_type === 'none' ? '#1E2330' : '#FBBF2420',
                      color: source.auth_type === 'none' ? '#4A5068' : '#FBBF24',
                    }}>
                      {source.auth_type}
                      {source.auth_key && `: ${source.auth_key}`}
                    </span>
                  </td>
                  <td style={{ ...COL, textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%',
                      backgroundColor: source.is_active ? '#00D97E' : '#FF4757',
                    }} />
                  </td>
                  <td style={{ ...COL, color: '#7B8399', fontSize: '0.7rem' }}>
                    {source.last_fetched_at || '—'}
                  </td>
                  <td style={{ ...COL, color: '#7B8399', fontSize: '0.7rem' }}>
                    {source.created_at || '—'}
                  </td>
                  <td style={{ ...COL, textAlign: 'center' }}>
                    <button
                      onClick={() => editId === source.id ? cancel() : startEdit(source)}
                      style={{
                        padding: '4px 10px', borderRadius: '6px', border: 'none',
                        fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer',
                        backgroundColor: editId === source.id ? '#FF4757' : '#1E2330',
                        color: editId === source.id ? '#fff' : '#7B8399',
                      }}
                    >
                      {editId === source.id ? 'Abbrechen' : 'Bearbeiten'}
                    </button>
                  </td>
                </tr>

                {editId === source.id && (
                  <tr>
                    <td colSpan={8} style={{
                      padding: '16px 12px', backgroundColor: '#1A1F2B',
                      borderBottom: '1px solid #1E2330',
                    }}>
                      <FormFields form={form} setForm={setForm} saving={saving} onSave={save} onCancel={cancel} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create form */}
      {creating && (
        <div style={{
          marginTop: '16px', padding: '20px', backgroundColor: '#151921',
          border: '1px solid #00D97E40', borderRadius: '12px',
        }}>
          <h3 style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '16px' }}>Neue API Quelle</h3>
          <FormFields form={form} setForm={setForm} saving={saving} onSave={save} onCancel={cancel} />
        </div>
      )}
    </div>
  );
}

function FormFields({ form, setForm, saving, onSave, onCancel }: {
  form: EditForm;
  setForm: (f: EditForm) => void;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div>
          <label style={LABEL}>Name</label>
          <input style={INPUT} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <label style={LABEL}>Source Key</label>
          <input style={INPUT} value={form.source_key} onChange={e => setForm({ ...form, source_key: e.target.value })} placeholder="gecko, twelve, polygon..." />
        </div>
        <div>
          <label style={LABEL}>Auth Type</label>
          <select
            style={{ ...INPUT, cursor: 'pointer' }}
            value={form.auth_type}
            onChange={e => setForm({ ...form, auth_type: e.target.value })}
          >
            <option value="none">Keine</option>
            <option value="query">Query Parameter</option>
            <option value="header">Header</option>
          </select>
        </div>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <label style={LABEL}>API URL</label>
        <input style={INPUT} value={form.api_url} onChange={e => setForm({ ...form, api_url: e.target.value })} placeholder="https://api.example.com/..." />
      </div>

      {form.auth_type !== 'none' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          <div>
            <label style={LABEL}>{form.auth_type === 'query' ? 'Parameter Name' : 'Header Name'}</label>
            <input style={INPUT} value={form.auth_key} onChange={e => setForm({ ...form, auth_key: e.target.value })} placeholder="apikey, X-Api-Key..." />
          </div>
          <div>
            <label style={LABEL}>API Key / Wert</label>
            <input style={INPUT} type="password" value={form.auth_value} onChange={e => setForm({ ...form, auth_value: e.target.value })} placeholder="Leer = nicht ändern" />
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.8rem' }}>
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={e => setForm({ ...form, is_active: e.target.checked })}
            style={{ accentColor: '#00D97E' }}
          />
          Aktiv
        </label>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px', borderRadius: '6px', border: '1px solid #2A3040',
              backgroundColor: 'transparent', color: '#7B8399', fontSize: '0.75rem',
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={onSave}
            disabled={saving || !form.name || !form.source_key || !form.api_url}
            style={{
              padding: '8px 16px', borderRadius: '6px', border: 'none',
              backgroundColor: '#00D97E', color: '#0B0E14', fontSize: '0.75rem',
              fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.5 : 1,
            }}
          >
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
